/*
  Immutable edits on the project structure tree by path (UI overhaul wave 1, screen 08 idea 3).
  A path is a list of steps from the root: [{ key: 'buildings', id }, { key: 'floors', id }, { key: 'rooms', id },
  { key: 'panels', id }, { key: 'devices', id }, { key: 'children', id } …].
  Used so a delete can be undone by putting exactly that node back where it was, even after other edits.
*/
import { countDeviceNodes, countNodeDevices } from './projectDetailModel';

const listOf = (container, key) => (Array.isArray(container?.[key]) ? container[key] : []);

/** → { node, index, parent } or null */
export function findAtPath(root, steps = []) {
  let container = root;
  let found = null;
  for (let level = 0; level < steps.length; level += 1) {
    const step = steps[level];
    const list = listOf(container, step.key);
    const index = list.findIndex((item) => item?.id === step.id);
    if (index < 0) return null;
    found = { node: list[index], index, parent: container };
    container = list[index];
  }
  return found;
}

/** → { next, node, index } or null when the path does not exist */
export function removeAtPath(root, steps = []) {
  if (!steps.length) return null;
  const [step, ...rest] = steps;
  const list = listOf(root, step.key);
  const index = list.findIndex((item) => item?.id === step.id);
  if (index < 0) return null;
  if (!rest.length) {
    return { next: { ...root, [step.key]: [...list.slice(0, index), ...list.slice(index + 1)] }, node: list[index], index };
  }
  const inner = removeAtPath(list[index], rest);
  if (!inner) return null;
  const nextList = [...list];
  nextList[index] = inner.next;
  return { next: { ...root, [step.key]: nextList }, node: inner.node, index: inner.index };
}

/** Puts `node` back into the list its path points to, at `index` (clamped). No-op if the parent is gone or it is already there. */
export function insertAtPath(root, steps = [], node, index) {
  if (!steps.length || !node) return root;
  const [step, ...rest] = steps;
  const list = listOf(root, step.key);
  if (!rest.length) {
    if (list.some((item) => item?.id === node.id)) return root;
    const at = Math.min(Math.max(Number(index) || 0, 0), list.length);
    return { ...root, [step.key]: [...list.slice(0, at), node, ...list.slice(at)] };
  }
  const parentIndex = list.findIndex((item) => item?.id === step.id);
  if (parentIndex < 0) return root;
  const nextChild = insertAtPath(list[parentIndex], rest, node, index);
  if (nextChild === list[parentIndex]) return root;
  const nextList = [...list];
  nextList[parentIndex] = nextChild;
  return { ...root, [step.key]: nextList };
}

/** BMS node path (ids under floor.devices) → steps after the floor. */
export const bmsPathSteps = (path = []) => path.map((id, level) => ({ key: level === 0 ? 'devices' : 'children', id }));

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** What disappears with a node, for the confirm dialog: ['3 floors', '46 devices'] */
export function describeStructureNode(node = {}) {
  const parts = [];
  const floors = listOf(node, 'floors').length;
  const rooms = listOf(node, 'rooms').length + listOf(node, 'floors').reduce((sum, floor) => sum + listOf(floor, 'rooms').length, 0);
  const panels = listOf(node, 'panels').length;
  if (floors) parts.push(plural(floors, 'floor'));
  if (rooms) parts.push(plural(rooms, 'room'));
  if (panels) parts.push(plural(panels, 'panel'));
  const nested = listOf(node, 'children').length;
  if (nested) parts.push(plural(nested, 'nested sub-item'));
  const devices = countNodeDevices(node) + countDeviceNodes(listOf(node, 'children'));
  if (devices) parts.push(plural(devices, 'device'));
  return parts;
}
